import { useCallback, useEffect, useState } from "react";
import { onCacheChange } from "../client";

export type CacheValue<T> = {
  value: T;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const toMessage = (error: unknown): string => (error instanceof Error ? error.message : "Something went wrong");

/**
 * Reads one cache item through `load` and follows it: every component that uses the same item re-reads it
 * when a client function writes it. `keys` are the cache keys that should trigger the re-read.
 */
export const useCacheValue = <T>(load: (forceRefresh?: boolean) => Promise<T>, keys: string[], initial: T): CacheValue<T> => {
  const [value, setValue] = useState<T>(initial);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const read = useCallback(
    async (forceRefresh: boolean) => {
      try {
        setValue(await load(forceRefresh));
        setError(null);
      } catch (e) {
        setError(toMessage(e));
      } finally {
        setIsLoading(false);
      }
    },
    [load],
  );

  useEffect(() => {
    void read(false);
    return onCacheChange((key) => {
      // A key that is a prefix of ours is a clear of many items.
      if (keys.some((k) => key === k || k.startsWith(key))) {
        void read(false);
      }
    });
  }, [read, keys]);

  const refresh = useCallback(() => read(true), [read]);

  return { value, isLoading, error, refresh };
};
